import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'app/common/service/api.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { isAuthPage, safeReturnUrl } from 'app/core/auth/return-url';
import { AccessRequest, OfficeContact, PasswordTokenInfo, SessionCheck, SessionResponse } from 'app/core/auth/session.models';
import { StorageService } from 'app/core/storage/storage.service';
import { IUser } from 'app/core/user/model/user.model';
import { UserService } from 'app/core/user/service/user.service';
import { catchError, defer, finalize, firstValueFrom, from, map, Observable, of, shareReplay, take, tap } from 'rxjs';

/** Karta, w której klient zalogował się albo przejął sesję innej karty (sesja bez „Zapamiętaj mnie”). */
export const TAB_FLAG = 'vz.tab';
/** Powód i email do ekranu „Sesja wygasła” (sessionStorage karty — bez danych w adresie). */
export const EXPIRED_KEY = 'vz.expired';

const REFRESH_LOCK = 'vz-refresh';

export interface ExpiredSession {
    reason: 'idle' | 'expired';
    email: string | null;
}

/**
 * Sesja klienta (Issue 41, 42; kontrakt 1.1.0 — /auth/*): logowanie emailem i hasłem, odnawianie tokenu dostępu
 * refresh tokenem (rotowanym przez backend), wylogowanie z unieważnieniem sesji, „Nie pamiętasz hasła?”,
 * ustawienie hasła z linku i „Zapytaj o dostęp”. Tokeny w localStorage — wspólne dla kart panelu; jedno
 * odnowienie naraz, także między kartami (Web Locks), bo zużyty refresh token unieważnia całą sesję.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly api = inject(ApiService);
    private readonly storage = inject(StorageService);
    private readonly userService = inject(UserService);
    private readonly router = inject(Router);

    private refreshing: Observable<string> | null = null;
    private office$: Observable<OfficeContact> | null = null;
    private expiring = false;

    /** Jest sesja do użycia: ważny token dostępu albo refresh token do odnowienia (bez wywołania API). */
    hasSession(): boolean {
        return !!this.storage.refreshToken || !AuthUtils.isTokenExpired(this.storage.accessToken);
    }

    signIn(email: string, password: string, remember: boolean): Observable<SessionResponse> {
        return this.api
            .post<SessionResponse>('/auth/login', { email: email.trim(), password, remember })
            .pipe(tap((session) => this.startSession(session)));
    }

    /** Ustawienie hasła z linku (zaproszenie albo reset) — po sukcesie klient jest zalogowany. */
    setPassword(token: string, password: string, remember: boolean): Observable<SessionResponse> {
        return this.api
            .post<SessionResponse>('/auth/set-password', { token, password, remember })
            .pipe(tap((session) => this.startSession(session)));
    }

    inspectPasswordToken(token: string): Observable<PasswordTokenInfo> {
        return this.api.post<PasswordTokenInfo>('/auth/password-token', { token });
    }

    forgotPassword(email: string): Observable<unknown> {
        return this.api.post('/auth/forgot-password', { email: email.trim() });
    }

    requestAccess(request: AccessRequest): Observable<unknown> {
        return this.api.post('/access-requests', request);
    }

    /** Biuro VAZUVI (telefon na ekranach przed zalogowaniem); bez połączenia — sama nazwa. */
    office(): Observable<OfficeContact> {
        if (!this.office$) {
            this.office$ = this.api.get<OfficeContact>('/public/office').pipe(
                catchError(() => of({ name: 'Biuro VAZUVI', phone: null, email: null })),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }

        return this.office$;
    }

    /**
     * Sesja na potrzeby strażnika trasy: ważny token, udane odnowienie albo „wygasła” (serwer odrzucił odnowienie).
     * Brak sieci przy odnowieniu nie kasuje sesji — po powrocie internetu wystarczy odświeżyć stronę.
     */
    check(): Observable<SessionCheck> {
        const access = this.storage.accessToken;

        if (access && !AuthUtils.isTokenExpired(access)) {
            return of('ok');
        }

        if (!this.storage.refreshToken) {
            return of('none');
        }

        return this.refresh().pipe(
            map((): SessionCheck => 'ok'),
            catchError((error) => {
                if (!isUnauthorized(error)) {
                    return of<SessionCheck>('none');
                }

                this.markExpired('expired');
                this.storage.clearSession();

                return of<SessionCheck>('expired');
            })
        );
    }

    /**
     * Nowy token dostępu. Jedno odnowienie naraz w karcie; między kartami blokada — karta, która czekała,
     * bierze tokeny zapisane przez drugą zamiast zużywać ten sam refresh token.
     */
    refresh(): Observable<string> {
        if (!this.refreshing) {
            const used = this.storage.refreshToken;

            this.refreshing = defer(() => from(this.withLock(() => this.refreshWith(used)))).pipe(
                finalize(() => (this.refreshing = null)),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }

        return this.refreshing;
    }

    /** Wylogowanie z menu: unieważnia sesję na serwerze (gdy się da) i czyści przeglądarkę. Zwraca, czy sesja była zapamiętana. */
    signOut(): Observable<boolean> {
        const refreshToken = this.storage.refreshToken;
        const remembered = this.hasSession() && this.storage.remember;

        this.storage.clearSession();

        if (!refreshToken) {
            return of(remembered);
        }

        return this.api.post('/auth/logout', { refreshToken }).pipe(
            map(() => remembered),
            catchError(() => of(remembered))
        );
    }

    /** Sesja bez „Zapamiętaj mnie” po zamknięciu wszystkich kart panelu — koniec bez komunikatu. */
    discard(): void {
        const refreshToken = this.storage.refreshToken;

        this.storage.clearSession();

        if (refreshToken) {
            this.api.post('/auth/logout', { refreshToken }).subscribe({ error: () => undefined });
        }
    }

    /**
     * Sesja wygasła (serwer odrzucił odnowienie albo 30 min bez aktywności): logowanie z komunikatem i powrotem
     * na tę samą stronę. Przy bezczynności sesja jest jeszcze ważna na serwerze — unieważniamy ją.
     */
    expire(reason: ExpiredSession['reason']): void {
        if (this.expiring || !this.onProtectedPage()) {
            return;
        }

        this.expiring = true;
        const returnUrl = safeReturnUrl(this.router.url);

        this.markExpired(reason);

        if (reason === 'idle') {
            this.discard();
        } else {
            this.storage.clearSession();
        }

        this.router
            .navigate(['/logowanie'], { queryParams: returnUrl && returnUrl !== '/' ? { powrot: returnUrl } : {} })
            .finally(() => (this.expiring = false));
    }

    /** Zapamiętuje w karcie powód wygaśnięcia i email do ekranu logowania. */
    markExpired(reason: ExpiredSession['reason']): void {
        const account: IUser | null = this.storage.isClientMode() ? this.userService.adminUser : this.userService.user;
        const expired: ExpiredSession = { reason, email: account?.username ?? null };

        try {
            sessionStorage.setItem(EXPIRED_KEY, JSON.stringify(expired));
        } catch {
            // Bez sessionStorage ekran logowania pokaże się bez komunikatu
        }
    }

    /** Komunikat „Sesja wygasła” dla ekranu logowania — jednorazowo. */
    takeExpired(): ExpiredSession | null {
        try {
            const value = sessionStorage.getItem(EXPIRED_KEY);
            sessionStorage.removeItem(EXPIRED_KEY);

            return value ? (JSON.parse(value) as ExpiredSession) : null;
        } catch {
            return null;
        }
    }

    /** Czy bieżąca strona wymaga sesji (a nie jest ekranem logowania). */
    onProtectedPage(): boolean {
        return !isAuthPage(this.router.url);
    }

    enterClientMode(user: IUser) {
        this.userService.user = user;
        this.storage.clientMode = true;
        this.router.navigate(['/']);
    }

    exitClientMode() {
        // take(1): adminUser$ to ReplaySubject — bez tego każde późniejsze ustawienie admina znów wyrzucałoby z podglądu klienta
        this.userService.adminUser$.pipe(take(1)).subscribe(user => {
            this.userService.user = user;
            this.storage.clientMode = false;
            this.storage.removeClientRelations();
            this.router.navigate(['/admin']);
        })
    }

    private startSession(session: SessionResponse): void {
        this.storage.clearSession();
        this.storeTokens(session);
        this.storage.clientMode = false;
        this.storage.lastActivity = Date.now();
        this.userService.user = session.user;

        try {
            sessionStorage.setItem(TAB_FLAG, '1');
            sessionStorage.removeItem(EXPIRED_KEY);
        } catch {
            // Bez sessionStorage sesja bez „Zapamiętaj mnie” skończy się przy przeładowaniu karty
        }
    }

    private storeTokens(session: SessionResponse): void {
        this.storage.accessToken = session.accessToken;
        this.storage.refreshToken = session.refreshToken;
        this.storage.remember = session.remember;
    }

    private async refreshWith(used: string): Promise<string> {
        const current = this.storage.refreshToken;

        // Inna karta odnowiła sesję, gdy ta czekała na blokadę — jej tokeny są już zapisane
        if (current && current !== used && !AuthUtils.isTokenExpired(this.storage.accessToken, 60)) {
            return this.storage.accessToken;
        }

        if (!current) {
            throw new HttpErrorResponse({ status: 401, error: { code: 'session_expired' } });
        }

        const session = await firstValueFrom(this.api.post<SessionResponse>('/auth/refresh', { refreshToken: current }));
        this.storeTokens(session);

        return session.accessToken;
    }

    private withLock<T>(task: () => Promise<T>): Promise<T> {
        const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;

        return locks ? locks.request(REFRESH_LOCK, task) : task();
    }
}

export function isUnauthorized(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
}
